import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';

let _initialized = false;

function initMermaid() {
  if (_initialized) return;
  mermaid.initialize({
    startOnLoad: false,
    securityLevel: 'loose',
    theme: 'default',
    mindmap: { useMaxWidth: true },
  });
  _initialized = true;
}

function sanitizeChart(raw) {
  if (!raw) return '';
  let s = raw
    .replace(/```(?:mermaid)?/gi, '')  // strip markdown code fences
    .trim();

  // Must start with a recognized diagram keyword
  if (!/^(mindmap|graph|flowchart|sequenceDiagram|classDiagram|erDiagram|gantt|pie)\b/i.test(s)) {
    return '';
  }

  // Normalize Windows line endings
  s = s.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Collapse more than two consecutive blank lines
  s = s.replace(/\n{3,}/g, '\n\n');

  return s;
}

let _uid = 0;

export default function MindMapViewer({ chart }) {
  const wrapperRef = useRef(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const clean = sanitizeChart(chart);
    if (!clean || !wrapperRef.current) {
      setError(clean ? '' : 'No valid diagram syntax received from the analysis.');
      return;
    }

    setLoading(true);
    setError('');

    // Mermaid requires the target element to be in the DOM.
    // We clear it first, then inject a fresh div to avoid stale SVG conflicts.
    wrapperRef.current.innerHTML = '';
    const id = `mm-${++_uid}`;
    const el = document.createElement('div');
    el.id = id;
    el.style.display = 'none';
    document.body.appendChild(el);

    initMermaid();

    mermaid
      .render(id, clean)
      .then(({ svg }) => {
        if (wrapperRef.current) {
          wrapperRef.current.innerHTML = svg;
          // Make SVG fill container and be responsive
          const svgEl = wrapperRef.current.querySelector('svg');
          if (svgEl) {
            svgEl.setAttribute('width', '100%');
            svgEl.style.maxWidth = '100%';
            svgEl.style.height = 'auto';
          }
        }
        setError('');
        setLoading(false);
      })
      .catch((err) => {
        console.error('[MindMap] Render failed:', err);
        setError('Diagram could not be rendered. The LLM may have returned malformed syntax.');
        setLoading(false);
        if (wrapperRef.current) wrapperRef.current.innerHTML = '';
      })
      .finally(() => {
        // Clean up the off-screen element Mermaid may have created
        const stale = document.getElementById(id);
        if (stale) stale.remove();
      });

    return () => {
      // Cleanup on unmount / chart change
      const stale = document.getElementById(id);
      if (stale) stale.remove();
    };
  }, [chart]);

  if (!chart) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
        Mind map will appear after portfolio analysis runs.
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', minHeight: 120 }}>
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0', color: 'var(--text-muted)', fontSize: 12 }}>
          <span className="spinner" /> Rendering diagram…
        </div>
      )}

      {error && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ padding: '8px 12px', background: 'var(--warning-bg)', border: '1px solid #fcd34d', borderRadius: 'var(--r)', fontSize: 12, color: '#92400e', marginBottom: 10 }}>
            ⚠️ {error}
          </div>
          {/* Fallback: show raw syntax in a readable code block */}
          <pre style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: 14, fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap', color: 'var(--text-2)', lineHeight: 1.6 }}>
            {sanitizeChart(chart) || chart}
          </pre>
        </div>
      )}

      <div ref={wrapperRef} style={{ overflow: 'auto' }} />
    </div>
  );
}
